import React, {useState, useEffect, useCallback} from 'react';
import {withTranslation} from 'react-i18next';
import {Panel} from "primereact/panel";
import {Badge} from "primereact/badge";
import currencyFormatter from "currency-formatter";
import moment from "moment/moment";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {ConfirmDialog} from "primereact/confirmdialog";
import axios from "axios";
import Cookies from "js-cookie";
import HelpComponent from "../HelpComponent";
import {closeMercureSubscription, openMercureSubscription} from "../../util/mercure";

const SubscriptionFormComponent = (props) => {

  const {t} = props;
  const [loading, setLoading] = useState(false);
  const [subscriptionplan, setSubscriptionplan] = useState([]);
  const [subscriptionplanActive, setSubscriptionplanActive] = useState(null);
  const [pendingSubscription, setPendingSubscription] = useState(null);
  const [allowedActions, setAllowedActions] = useState([]);
  const [subscriptionPaymentLink, setSubscriptionPaymentLink] = useState('');
  const [subscriptionPaymentPlanId, setSubscriptionPaymentPlanId] = useState(null);
  const [subscriptionPaymentPending, setSubscriptionPaymentPending] = useState(false);
  const [showSubscriptionPaymentDialog, setShowSubscriptionPaymentDialog] = useState(false);
  const [subscriptionConfirmAction, setSubscriptionConfirmAction] = useState(null);
  const rerenderSubscriptionPage = window.App.state.rerenderSubscriptionPage;

  const subscriptionHeaders = useCallback(() => {
    return {
      'Authorization': 'Bearer ' + Cookies.get("userToken"),
      "Sys-name": Cookies.get("instanceId"),
      "Preferred-locale": Cookies.get("language")
    }
  }, [])

  const subscriptions = useCallback(() => {
    setLoading(true)
    const activeSubscriptionRequest = axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "system/settings/subscription",
      params: {},
      headers: subscriptionHeaders()
    })

    const subscriptionPlansRequest = axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "subscription-plan",
      params: {},
      headers: subscriptionHeaders()
    })

    Promise.allSettled([activeSubscriptionRequest, subscriptionPlansRequest])
      .then(([subscriptionResult, subscriptionPlanResult]) => {
        let activePlanId = null;
        const pendingPlanIds = [];

        if (subscriptionResult.status === 'fulfilled') {
          const subscriptionData = subscriptionResult.value.data.data || null;
          setSubscriptionplanActive(subscriptionData)
          setPendingSubscription(subscriptionData?.pendingSubscription || null)
          setAllowedActions(Array.isArray(subscriptionData?.allowedActions) ? subscriptionData.allowedActions : [])
          activePlanId = subscriptionData?.subscription_plan?.id || null

          if (Array.isArray(subscriptionData?.pendingSubscription)) {
            subscriptionData.pendingSubscription.forEach((item) => {
              const planId = item?.planId || item?.id || null;
              if (planId) {
                pendingPlanIds.push(planId)
              }
            })
          } else if (subscriptionData?.pendingSubscription) {
            const planId = subscriptionData.pendingSubscription?.planId || subscriptionData.pendingSubscription?.id || null;
            if (planId) {
              pendingPlanIds.push(planId)
            }
          }
        } else {
          setSubscriptionplanActive(null)
          setPendingSubscription(null)
          setAllowedActions([])
        }

        if (subscriptionPlanResult.status === 'fulfilled') {
          const subscriptionPlans = subscriptionPlanResult.value.data.data.items || [];
          const filteredPlans = subscriptionPlans.filter((plan) => {
            if (plan?.visible_for_plan_list) {
              return true;
            }

            if (activePlanId && plan?.id === activePlanId) {
              return true;
            }

            return pendingPlanIds.includes(plan?.id);
          })

          setSubscriptionplan(filteredPlans)
        } else {
          window.App.toastShow('error', t('error'), subscriptionPlanResult.reason.toString(), '', subscriptionPlanResult.reason.response);
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [subscriptionHeaders, t])

  const getSubscriptionStatus = () => {
    return String(subscriptionplanActive?.status || '').toUpperCase()
  }

  const hasCurrentSubscription = () => {
    const status = getSubscriptionStatus()
    const hasPlanId = !!subscriptionplanActive?.subscription_plan?.id
    return hasPlanId && (status === 'ACTIVE' || status === 'PAUSED')
  }

  const isActiveSubscription = () => {
    return getSubscriptionStatus() === 'ACTIVE' && !!subscriptionplanActive?.subscription_plan?.id
  }

  const isPausedSubscription = () => {
    return getSubscriptionStatus() === 'PAUSED' && !!subscriptionplanActive?.subscription_plan?.id
  }

  const isCurrentPlan = (plan) => {
    return hasCurrentSubscription() && subscriptionplanActive.subscription_plan.id === plan.id
  }

  const isEndingPlan = (plan) => {
    if (Array.isArray(pendingSubscription)) {
      return pendingSubscription.some((item) => (item?.planId || item?.id) === plan.id)
    }

    return (pendingSubscription?.planId || pendingSubscription?.id) === plan.id
  }

  const hasPendingSubscription = () => {
    if (Array.isArray(pendingSubscription)) {
      return pendingSubscription.some((item) => !!(item?.planId || item?.id))
    }

    return !!(pendingSubscription?.planId || pendingSubscription?.id)
  }

  const hasAllowedAction = (action) => {
    return allowedActions.includes(action)
  }

  const formatPrice = (plan) => {
    return currencyFormatter.format(
      plan.amount, {
        precision: t('currency_precision'),
        symbol: t('currency_symbol'),
        decimal: t('currency_decimal'),
        thousand: t('currency_thousand'),
      }
    )
  }

  const formatPausedPrice = (plan) => {
    const amountToDisplay = plan?.paused_amount ?? plan?.amount

    return currencyFormatter.format(
      amountToDisplay, {
        precision: t('currency_precision'),
        symbol: t('currency_symbol'),
        decimal: t('currency_decimal'),
        thousand: t('currency_thousand'),
      }
    )
  }

  const formatMoneyAmount = (amount) => {
    const numericAmount = Number(amount)
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0

    return currencyFormatter.format(
      safeAmount, {
        precision: t('currency_precision'),
        symbol: t('currency_symbol'),
        decimal: t('currency_decimal'),
        thousand: t('currency_thousand'),
      }
    )
  }

  const isBasicPlan = (plan) => {
    return String(plan?.code || '').toUpperCase() !== 'FREE_S'
  }

  const getUsageAmountLabel = (status) => {
    return String(status || '').toUpperCase() === 'FINAL'
      ? t('subscription_usage_extra_final')
      : t('subscription_usage_extra_estimated')
  }

  const formatFrequency = (frequency) => {
    if (frequency === 'MONTHLY') {
      return t('monthly')
    }
    if (frequency === 'YEARLY') {
      return t('yearly')
    }
    if (frequency === 'VARIED') {
      return t('varied')
    }

    return frequency
  }

  const formatPricePeriodLabel = (plan) => {
    const currency = String(plan?.currency || '').toLowerCase()
    const vatSuffix = currency === 'huf' ? ` + ${t('vat')}` : ''
    const frequency = String(plan?.frequency || '').toUpperCase()

    if (frequency === 'VARIED') {
      return `${plan.currency}${vatSuffix}`
    }

    return `${plan.currency}${vatSuffix} / ${formatFrequency(plan.frequency)}`
  }

  const formatCurrencyLabel = (currencyCode) => {
    const currency = String(currencyCode || '').toLowerCase()
    return currency === 'huf' ? `${currencyCode} + ${t('vat')}` : currencyCode
  }

  const formatDate = (date) => {
    return moment.utc(date).local().format(t('YYYY-MM-DD'))
  }

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + Cookies.get("userToken"),
        "Sys-name": Cookies.get("instanceId"),
        "Preferred-locale": Cookies.get("language")
      }
    })
  }

  const refreshSubscriptionSidebar = () => {
    window.App.setState({
      rerenderSubscriptionPage: Math.random(),
      sidebarSubscriptionForm: true
    })
  }

  const closeSubscriptionConfirmDialog = () => {
    setSubscriptionConfirmAction(null)
    refreshSubscriptionSidebar()
  }

  const onConfirmSubscriptionAction = () => {
    if (!subscriptionConfirmAction?.type) {
      return;
    }

    window.App.setState({sidebarSubscriptionForm: true})
    setSubscriptionConfirmAction(null)
    setLoading(true)

    if (subscriptionConfirmAction.type === 'cancel') {
      apiCall('post', 'system/settings/subscription/cancel', {})
        .then(response => {
          window.App.toastShow('success', t('success'), response.data.message)
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
      return;
    }

    if (subscriptionConfirmAction.type === 'pause' && subscriptionConfirmAction.planId) {
      apiCall('post', 'system/settings/subscription/pause', {})
        .then(response => {
          window.App.toastShow('success', t('success'), response.data.message)
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
      return;
    }

    if (subscriptionConfirmAction.type === 'resume' && subscriptionConfirmAction.planId) {
      apiCall('post', 'system/settings/subscription/resume', {})
        .then(response => {
          window.App.toastShow('success', t('success'), response.data.message)
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
      return;
    }

    if (subscriptionConfirmAction.type === 'subscribe' && subscriptionConfirmAction.planId) {
      apiCall('post', 'system/settings/subscription/subscribe/' + subscriptionConfirmAction.planId, {})
        .then(response => {
          window.App.toastShow('success', t('success'), response.data.message)
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
          window.App.setState({
            rerenderSubscriptionPage: Math.random()
          })
        })
      return;
    }

    setLoading(false)
  }

  const getSubscriptionConfirmMessage = () => {
    if (subscriptionConfirmAction?.type === 'cancel') {
      return t('confirm_subscription_cancel')
    }
    if (subscriptionConfirmAction?.type === 'pause') {
      return t('confirm_subscription_pause')
    }
    if (subscriptionConfirmAction?.type === 'resume') {
      return t('confirm_subscription_resume')
    }
    return t('confirm_subscription_start')
  }

  const extractPaymentDataFromMercurePayload = (rawPayload) => {
    const parseJson = (value) => {
      if (typeof value !== 'string') {
        return null;
      }
      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    };
    const normalizeUrl = (value) => {
      if (typeof value !== 'string') {
        return '';
      }
      return value.replace(/\\\//g, '/');
    };

    let payload = rawPayload;

    if (typeof payload === 'string') {
      const parsed = parseJson(payload);
      if (parsed !== null) {
        payload = parsed;
      } else {
        const wrappedMatch = payload.match(/data:\s*['"](.+)['"]$/s);
        if (wrappedMatch && wrappedMatch[1]) {
          const wrappedParsed = parseJson(wrappedMatch[1]);
          payload = wrappedParsed !== null ? wrappedParsed : wrappedMatch[1];
        }
      }
    }

    if (typeof payload === 'object' && payload !== null) {
      const latestCheckoutUrl = payload?.affectedSubscription?.meta?.latest_checkout_url;
      const firstPayCheckoutUrl = payload?.affectedSubscription?.meta?.first_pay_checkout_url;
      const directLink = payload?.link;
      const foundUrl = latestCheckoutUrl || firstPayCheckoutUrl || directLink;
      if (foundUrl) {
        const status = payload?.affectedSubscription?.status || payload?.status || '';
        return {
          paymentLink: normalizeUrl(foundUrl),
          subscriptionPlanId: payload?.affectedSubscription?.subscriptionPlan?.id || null,
          isPending: status === 'PENDING'
        };
      }
    }

    if (typeof rawPayload === 'string') {
      const escapedUrlMatch = rawPayload.match(/https:\\\/\\\/[^"'\s,}]+/);
      if (escapedUrlMatch && escapedUrlMatch[0]) {
        return {
          paymentLink: normalizeUrl(escapedUrlMatch[0]),
          subscriptionPlanId: null,
          isPending: rawPayload.includes('"status":"PENDING"')
        };
      }
      const plainUrlMatch = rawPayload.match(/https:\/\/[^"'\s,}]+/);
      if (plainUrlMatch && plainUrlMatch[0]) {
        return {
          paymentLink: plainUrlMatch[0],
          subscriptionPlanId: null,
          isPending: rawPayload.includes('"status":"PENDING"')
        };
      }
    }

    return null;
  };

  useEffect(() => {
    const topic = process.env.REACT_APP_API_PROTOCOL
      + process.env.REACT_APP_API_HOST.replace('/api/', '/')
      + Cookies.get("instanceId")
      + "/api/system/settings/subscription";
    const eventSource = openMercureSubscription({
      topics: [topic],
      onMessage: (event) => {
        try {
          const paymentData = extractPaymentDataFromMercurePayload(event?.data || '');
          if (paymentData?.paymentLink && paymentData?.isPending) {
            setSubscriptionPaymentLink(paymentData.paymentLink);
            setSubscriptionPaymentPlanId(paymentData.subscriptionPlanId);
            setSubscriptionPaymentPending(true);
            setShowSubscriptionPaymentDialog(true);
          } else {
            setSubscriptionPaymentLink('');
            setSubscriptionPaymentPlanId(null);
            setSubscriptionPaymentPending(false);
            setShowSubscriptionPaymentDialog(false);
          }
        } catch (error) {
          console.log('subscription mercure parse error', error);
        }
        window.App.setState({
          rerenderSubscriptionPage: Math.random(),
          rerenderNotificationsList: Math.random()
        })
      }
    });
    if (!eventSource) {
      return undefined;
    }

    console.log('feliratkozás történt: ' + topic)
    return () => {
      closeMercureSubscription(eventSource);
      console.log('leiratkozás történt: ' + topic)
    };
  }, []);

  useEffect(() => {
    document.title = t('subscription') + ' - ' + process.env.REACT_APP_TITLE;
    window.App.help('/subscription')
    subscriptions()
  }, [rerenderSubscriptionPage, subscriptions, t]);

  useEffect(() => {
    window.App.setState({subscriptionPaymentDialogVisible: showSubscriptionPaymentDialog});
    return () => {
      window.App.setState({subscriptionPaymentDialogVisible: false});
    };
  }, [showSubscriptionPaymentDialog]);

  useEffect(() => {
    window.App.setState({subscriptionConfirmDialogVisible: !!subscriptionConfirmAction});
    return () => {
      window.App.setState({subscriptionConfirmDialogVisible: false});
    };
  }, [subscriptionConfirmAction]);

  return (
    <>
      <ConfirmDialog
        group="declarative"
        visible={!!subscriptionConfirmAction}
        onHide={closeSubscriptionConfirmDialog}
        acceptLabel={t('yes')}
        rejectLabel={t('no')}
        header={t('confirm_required')}
        message={getSubscriptionConfirmMessage()}
        icon="pi pi-exclamation-triangle"
        accept={onConfirmSubscriptionAction}
        reject={closeSubscriptionConfirmDialog}
      />
      <Dialog
        header={t('subscription_continue')}
        visible={showSubscriptionPaymentDialog}
        onHide={() => setShowSubscriptionPaymentDialog(false)}
        dismissableMask={true}
        style={{width: '32rem', maxWidth: '95vw'}}
      >
        <p className={"p-mt-0 p-mb-3"}>{t('subscription_activation_payment_required')}</p>
        <div className={"p-text-right"}>
          <Button
            type={"button"}
            icon={"pi pi-external-link"}
            label={t('subscription_start_payment')}
            loading={loading}
            className={"p-button-primary"}
            disabled={!subscriptionPaymentLink}
            onClick={() => {
              if (subscriptionPaymentLink) {
                window.open(subscriptionPaymentLink, '_blank', 'noopener,noreferrer');
                setShowSubscriptionPaymentDialog(false);
              }
            }}
          />
        </div>
      </Dialog>
      <Panel className={"p-mt-3 subscription-form"}>
        <div className={"p-grid"}>
          <div className={"p-col-12 p-lg-6 p-col-align-center"}>
            <h2 className={"p-m-0"}>
              {t('subscription')}
              {isActiveSubscription() ? (
                <Badge value={t('active')} severity={"success"} className={"p-ml-2"}
                       style={{position: 'relative', top: '-4px'}}></Badge>
              ) : isPausedSubscription() ? (
                <Badge value={t('paused')} severity={"warning"} className={"p-ml-2"}
                       style={{position: 'relative', top: '-4px'}}></Badge>
              ):(
                <Badge value={t('inactive')} severity={"danger"} className={"p-ml-2"}
                       style={{position: 'relative', top: '-4px'}}></Badge>
              )}
            </h2>
          </div>
          <div className={"p-col-12 p-lg-6 p-col-align-center p-text-right"}>
            <Button type="button"
                    loading={loading}
                    icon={"pi pi-times"}
                    onClick={() => {
                      window.App.setState({sidebarSubscriptionForm: false});
                    }}
                    className="p-button-secondary close p-ml-2">
            </Button>
          </div>
        </div>
        <HelpComponent type={"edit_box"}/>
        <div className={"subscription-plans__header"}>
          <div>
            <h3>{t('subscriptionplans')}</h3>
            {hasCurrentSubscription() &&
              <div className={"subscription-plans__current"}>
                <i className={"pi pi-check-circle"}></i>
                <span>{t('active_subscriptionplan')}: {subscriptionplanActive.subscription_plan.name}</span>
              </div>
            }
          </div>
        </div>

        {subscriptionplan && subscriptionplan[0] ? (
          <div className={"subscription-plans"}>
            {subscriptionplan.map((plan) => {
              const currentPlan = isCurrentPlan(plan);
              const activePlan = currentPlan && isActiveSubscription();
              const pausedPlan = currentPlan && isPausedSubscription();
              const endingPlan = isEndingPlan(plan);
              const usageSummary = activePlan && isBasicPlan(plan) ? subscriptionplanActive?.usageSummary : null;
              const canChangeSubscriptionPlan = hasAllowedAction('change_subscription_plan');
              const canPauseSubscription = hasAllowedAction('pause_subscription');
              const canCancelSubscription = hasAllowedAction('cancel_subscription');
              const canResumeSubscription = hasAllowedAction('resume_subscription');
              const pausedAt = subscriptionplanActive?.pausedAt || subscriptionplanActive?.paused_at;
              const pauseUntil = subscriptionplanActive?.pauseUntil || subscriptionplanActive?.pause_until;

              if (!currentPlan && !endingPlan && !canChangeSubscriptionPlan && !(subscriptionPaymentPlanId === plan.id && subscriptionPaymentLink && subscriptionPaymentPending)) {
                return null;
              }

              return (
                <div key={plan.id} className={"subscription-plan" + (currentPlan ? " subscription-plan--active" : "")}
                style={{borderRadius: '4px'}}>
                  <div className={"p-grid p-m-0  subscription-plan__grid"}>
                    <div className={"p-col-8  p-col-align-center subscription-plan__name-col"}>
                      <h4>
                        {plan.name}
                        {currentPlan &&
                          <Badge value={pausedPlan ? t('paused') : (hasPendingSubscription() ? t('subscription_cancellation_pending') : t('active'))}
                                 severity={pausedPlan ? "warning" : (hasPendingSubscription() ? "warning" : "success")}
                                 style={{position: 'relative', top: '-4px'}}
                                 className={"p-ml-2"}></Badge>
                        }
                        {!currentPlan && endingPlan &&
                          <Badge value={t('subscription_in_progress')} severity={"warning"} style={{position: 'relative', top: '-4px'}}
                                 className={"p-ml-2"}></Badge>
                        }
                      </h4>
                    </div>
                    <div
                      className={"p-col-4  p-col-align-center p-text-right subscription-plan__period-col p-text-nowrap"}>
                      {activePlan &&
                        <div className={"subscription-plan__period"}>
                          <span  className={"p-m-0 p-p-0"}  style={{lineHeight: '8px'}}>
                            <small>{t('subscription_end_date')}</small>
                            {formatDate(subscriptionplanActive.next_payment_date)}
                          </span>
                        </div>
                      }
                      {pausedPlan &&
                        <div className={"subscription-plan__period"}>
                          {pausedAt && (
                            <span className={"p-m-0 p-p-0"} style={{lineHeight: '8px'}}>
                              <small>{t('subscription_paused_from')}</small>
                              {formatDate(pausedAt)}
                            </span>
                          )}
                          {pauseUntil && (
                            <span className={"p-m-0 p-p-0"} style={{lineHeight: '8px'}}>
                              <small>{t('subscription_paused_until')}</small>
                              {formatDate(pauseUntil)}
                            </span>
                          )}
                        </div>
                      }
                    </div>
                  </div>
                  {usageSummary && (
                    <div className={"p-grid p-m-0 subscription-plan__grid subscription-plan__grid--usage"}>
                      <div className={"p-col-12 p-md-6 p-lg-2 p-col-align-center"}>
                        <div className={"subscription-plan__usage-item"}>
                          <small>{t('subscription_usage_base_fee')}</small>
                          <div className={"subscription-plan__usage-value"}>
                            <span>{formatMoneyAmount(usageSummary.baseAmount)}</span>
                            <small>{formatCurrencyLabel(plan.currency)}</small>
                          </div>
                        </div>
                      </div>
                      <div className={"p-col-12 p-md-6 p-lg-4 p-col-align-center"}>
                        <div className={"subscription-plan__usage-item"}>
                          <small>{t('subscription_usage_overage_units')}</small>
                          <div className={"subscription-plan__usage-value"}>
                            <span>{usageSummary.overageQuantity || 0}</span>
                            <small>{t('unit')}</small>
                          </div>
                        </div>
                      </div>
                      <div className={"p-col-12 p-md-6 p-lg-3 p-col-align-center"}>
                        <div className={"subscription-plan__usage-item"}>
                          <small>{getUsageAmountLabel(usageSummary.status)}</small>
                          <div className={"subscription-plan__usage-value"}>
                            <span>{formatMoneyAmount(usageSummary.usageAmount)}</span>
                            <small>{formatCurrencyLabel(plan.currency)}</small>
                          </div>
                        </div>
                      </div>
                      <div className={"p-col-12 p-md-6 p-lg-3 p-col-align-center"}>
                        <div className={"subscription-plan__usage-item"}>
                          <small>{t('subscription_usage_estimated_renewal_total')}</small>
                          <div className={"subscription-plan__usage-value"}>
                            <span>{formatMoneyAmount(usageSummary.estimatedRenewalTotal)}</span>
                            <small>{formatCurrencyLabel(plan.currency)}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {plan.code !== 'FREE_S' &&
                    <div className={"subscription-plan__grid subscription-plan__grid--usage"}>
                    <span className={"subscription-plan__usage-item"}>
                      <small style={{fontWeight: "normal"}}>{plan.public_description}</small>
                    </span>
                    </div>
                  }
                  <div className={"p-grid p-m-0  subscription-plan__grid subscription-plan__grid--actions"}>
                    <div className={"p-col-12  p-lg-4 p-col-align-center subscription-plan__code-col"}>
                      <div className={"subscription-plan__code"}>{plan.code}</div>
                    </div>
                    <div className={"p-col-12  p-lg-3 p-col-align-center subscription-plan__price-col"}>
                      <div className={"subscription-plan__price subscription-plan__price--paused"}>
                        <span>{formatPausedPrice(plan)}</span>
                        <div className={"subscription-plan__price-meta"}>
                          <small>{formatPricePeriodLabel(plan)}</small>
                          <small className={"subscription-plan__price-label"}>{t('paused_amount')}</small>
                        </div>
                      </div>
                    </div>
                    <div className={"p-col-12  p-lg-3 p-col-align-center subscription-plan__price-col"}>
                      <div className={"subscription-plan__price"}>
                        <span>{formatPrice(plan)}</span>
                        <small>{formatPricePeriodLabel(plan)}</small>
                      </div>
                    </div>
                    <div className={"p-col-12  p-lg-2 p-col-align-center p-text-right subscription-plan__action-col"}>
                      {currentPlan ? (
                        <div className={"subscription-plan__action-buttons"}>
                          {activePlan && canPauseSubscription && (
                            <Button type={"button"} icon={"pi pi-pause"}
                                    tooltip={t('pause_subscription')}
                                    tooltipOptions={{position: 'top'}}
                                    aria-label={t('pause_subscription')}
                                    className={"p-button-warning"}
                                    loading={loading}
                                    disabled={hasPendingSubscription()}
                                    onClick={() => {
                                      const pauseTargetId = subscriptionplanActive?.id || plan.id
                                      setSubscriptionConfirmAction({type: 'pause', planId: pauseTargetId})
                                    }}
                            />
                          )}
                          {pausedPlan && canResumeSubscription && (
                            <Button type={"button"} icon={"pi pi-play"}
                                    tooltip={t('resume_subscription')}
                                    tooltipOptions={{position: 'top'}}
                                    aria-label={t('resume_subscription')}
                                    className={"p-button-success"}
                                    loading={loading}
                                    disabled={hasPendingSubscription()}
                                    onClick={() => {
                                      const resumeTargetId = subscriptionplanActive?.id || plan.id
                                      setSubscriptionConfirmAction({type: 'resume', planId: resumeTargetId})
                                    }}
                            />
                          )}
                          {activePlan && canCancelSubscription && (
                            <Button type={"button"} icon={"pi pi-times"}
                                    tooltip={t('cancel_subscription')}
                                    tooltipOptions={{position: 'top'}}
                                    aria-label={t('cancel_subscription')}
                                    className={"p-button-danger"}
                                    loading={loading}
                                    disabled={hasPendingSubscription()}
                                    onClick={() => {
                                      setSubscriptionConfirmAction({type: 'cancel'})
                                    }}
                            />
                          )}
                        </div>
                      ) : subscriptionPaymentPlanId === plan.id && subscriptionPaymentLink && subscriptionPaymentPending ? (
                        <Button type={"button"} icon={"pi pi-external-link"} label={t('subscription_open_payment')}
                                className={"p-button-warning p-d-block w-100"}
                                loading={loading}
                                onClick={() => {
                                  window.open(subscriptionPaymentLink, '_blank', 'noopener,noreferrer');
                                  setShowSubscriptionPaymentDialog(false);
                                }}
                        />
                      ) : endingPlan ? (
                        <Button type={"button"} icon={"pi pi-clock"}
                                tooltip={t('subscription_in_progress')}
                                tooltipOptions={{position: 'top'}}
                                aria-label={t('subscription_in_progress')}
                                className={"p-button-secondary"}
                                disabled={true}
                        />
                      ) : canChangeSubscriptionPlan ? (
                        <Button type={"button"} icon={"pi pi-play"}
                                tooltip={hasCurrentSubscription() ? t('change_subscription_plan') : t('start_subscription')}
                                tooltipOptions={{position: 'top'}}
                                aria-label={hasCurrentSubscription() ? t('change_subscription_plan') : t('start_subscription')}
                                className={"p-button-success"} loading={loading}
                                onClick={() => {
                                  setSubscriptionConfirmAction({type: 'subscribe', planId: plan.id})
                                }}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className={"subscription-plans__empty"}>{t('empty_message')}</div>
        )}
      </Panel>
    </>
  )
}
export default withTranslation('common')(SubscriptionFormComponent);
